/**
 * Renders an instruction as a card the operator can read and run: the narration
 * (what / why / reversible / what the host checks / what to do if it fails) and a
 * button that creates the instruction. People do not remember the release
 * process, so every action explains itself here rather than relying on the
 * operator knowing what a button does (DESIGN §11.1 / the guided-UI philosophy).
 *
 * The narration is laid out in reading order: the title and what it does carry
 * the weight, why it exists sits under them in a quieter tone, and the
 * preconditions and failure advice are a labelled fact list below a rule — so a
 * reader who already knows the step can stop after the first line, and one who
 * does not can keep going without the card shouting all five fields at once.
 * The reversibility verdict is also a chip beside the title: it is the one fact
 * an operator must not miss before pressing anything.
 *
 * The card also reports back. Creating an instruction changes nothing on this
 * page by itself — the host has to act — so the card watches the newest
 * instruction of its kind and shows it queued (with why, if the host is not
 * going to pick it up), running, or how the last run ended. While one is in
 * flight the button is disabled: a second click would only queue a duplicate.
 */
import { Box, Button, Card, CardActions, CardContent, Chip, CircularProgress, Divider, Stack, Tooltip, Typography } from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { narrationFor, type HostRole, type InstructionKind, type Narration } from '@finvari/deployer';
import { useInstructionAction } from '../hooks';
import { ActivityAlert } from './ActivityAlert';
import { FactList, type Fact } from './FactList';
import type { InstructionRow } from '../lib/instructionActivity';

export interface ActionCardProps {
    host: HostRole;
    kind: InstructionKind;
    params?: Record<string, unknown>;
    release?: string;
    /** A reason the action is currently unavailable (disables the button). */
    disabledReason?: string;
    /** Replaces the narration's generic title, e.g. to name the version a start would produce. */
    title?: string;
    /** Marks the card as the page's main call to action (a primary accent on its edge). */
    emphasis?: boolean;
    /**
     * Narration to show instead of the kind's own. For a kind that covers several
     * actions — `prepare.step` is one kind for five steps — the kind's narration
     * can only describe all of them at once, which is no use on a card offering
     * one. `PREPARE_STEP_NARRATION` is what a step card passes here.
     */
    narration?: Narration;
    /**
     * Narrows which instruction of this kind the card reports on, for the same
     * reason: without it a step card would show another step's outcome under its
     * own button.
     */
    match?: (row: InstructionRow) => boolean;
}

/**
 * The glanceable verdict from the narration's `reversible` sentence. The field
 * is written as "Yes", "Yes until X", or "No — …" (see `Narration`), so its
 * first word is the verdict and the rest is the explanation the fact list shows
 * in full; the chip only has to get the colour right.
 */
function reversibilityChip(reversible: string): { label: string; color: 'success' | 'warning' | 'default' } {
    if (reversible.startsWith('No')) return { label: 'Not reversible', color: 'warning' };
    if (reversible === 'Yes' || reversible.startsWith('Yes —')) return { label: 'Reversible', color: 'success' };
    return { label: 'Reversible with limits', color: 'default' };
}

export function ActionCard({
    host,
    kind,
    params,
    release,
    disabledReason,
    title,
    emphasis,
    narration,
    match,
}: ActionCardProps) {
    const n = narration ?? narrationFor(kind);
    const { run, sending, error, latest, activity, disabledReason: reason } = useInstructionAction(host, kind, {
        params,
        release,
        disabledReason,
        match,
    });
    const verdict = reversibilityChip(n.reversible);

    const facts: Fact[] = [
        { label: 'Reversible', value: n.reversible },
        ...(n.checked.length > 0
            ? [
                  {
                      label: 'Checks first',
                      value: (
                          <Stack component="ul" spacing={0.25} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                              {n.checked.map((c) => (
                                  <Stack component="li" key={c} direction="row" spacing={0.75} alignItems="flex-start">
                                      <CheckRounded sx={{ fontSize: 16, mt: '3px', color: 'text.secondary', flexShrink: 0 }} />
                                      <span>{c}</span>
                                  </Stack>
                              ))}
                          </Stack>
                      ),
                  },
              ]
            : []),
        { label: 'If it fails', value: n.ifItFails },
    ];

    const button = (
        <Button
            variant="contained"
            onClick={run}
            disabled={sending || !!reason}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
            {sending ? 'Sending…' : n.title}
        </Button>
    );

    return (
        <Card variant="outlined" sx={emphasis ? { borderLeft: 4, borderLeftColor: 'primary.main' } : undefined}>
            <CardContent>
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6">{title ?? n.title}</Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                                {n.what}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {n.why}
                            </Typography>
                        </Box>
                        <Chip size="small" variant="outlined" color={verdict.color} label={verdict.label} sx={{ flexShrink: 0 }} />
                    </Stack>
                    <Divider />
                    <FactList facts={facts} />
                    {latest && activity && <ActivityAlert host={host} row={latest} line={activity} variant="inline" />}
                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                </Stack>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2 }}>
                {reason ? (
                    <Tooltip title={reason}>
                        <span>{button}</span>
                    </Tooltip>
                ) : (
                    button
                )}
            </CardActions>
        </Card>
    );
}
